import React, { useEffect, useState } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import * as tenantUserActions from "src/redux/slices/tenantUserSlice";
import * as authAdminActions from "src/redux/slices/authAdminSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { toast } from "react-toastify";
import Button from "../ui/Button";
import Input from "../ui/Input";
import PasswordField from "../auth/PasswordField";
import { validatePassword } from "src/utils/authFormValidation";

const ResetPasswordCard: React.FC<{ variant?: "tenant" | "admin" }> = ({
  variant = "tenant",
}) => {
  const dispatch = useAppDispatch();
  const { changePassword: tenantChangePassword } = useAppSelector(
    (state) => state.tenantUser
  );
  const { changePassword: adminChangePassword } = useAppSelector(
    (state) => state.authAdmin
  );
  const changePassword =
    variant === "admin" ? adminChangePassword : tenantChangePassword;

  const [fields, setFields] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ✅ Side-effects after password change
  useEffect(() => {
    if (changePassword?.data) {
      toast.success("Password changed successfully");
      dispatch(
        variant === "admin"
          ? authAdminActions.resetChangePassword()
          : tenantUserActions.resetChangeTenantUserPassword()
      );
      setFields({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } else if (changePassword?.error) {
      toast.error(`Error: ${changePassword.error}`);
    }
  }, [changePassword]);

  // ✅ Validation before submitting
  const validate = () => {
    const newErrors = { ...errors };
    let valid = true;

    if (!fields.oldPassword.trim()) {
      newErrors.oldPassword = "Old password is required";
      valid = false;
    } else newErrors.oldPassword = "";

    const passwordRuleError = validatePassword(fields.newPassword);
    if (passwordRuleError) {
      newErrors.newPassword = passwordRuleError;
      valid = false;
    } else if (fields.newPassword === fields.oldPassword) {
      newErrors.newPassword =
        "New password must be different from old password";
      valid = false;
    } else newErrors.newPassword = "";

    if (fields.newPassword !== fields.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      valid = false;
    } else newErrors.confirmPassword = "";

    setErrors(newErrors);
    return valid;
  };

  // ✅ Handle form submit
  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        oldPassword: fields.oldPassword,
        newPassword: fields.newPassword,
      };
      dispatch(
        variant === "admin"
          ? authAdminActions.changePasswordRequest(payload)
          : tenantUserActions.changeTenantUserPasswordRequest(payload)
      );
    } catch (error: any) {
      toast.error("Failed to change password");
      console.error(error);
    }
  };

  return (
    <>
      <form className="space-y-4 max-w-md mx-auto" onSubmit={handleReset}>
        <Input
          label={"Old Password"}
          name="oldPassword"
          type={showOldPassword ? "text" : "password"}
          placeholder="Enter old password"
          value={fields.oldPassword}
          onChange={(e) =>
            setFields({ ...fields, oldPassword: e.target.value })
          }
          startAdornment={<Lock size={16} />}
          endAdornment={
            <button
              type="button"
              onClick={() => setShowOldPassword((s) => !s)}
              className="flex items-center"
            >
              {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
          error={!!errors.oldPassword}
          helperText={errors.oldPassword}
        />

        <PasswordField
          label="New Password"
          placeholder="Enter new password"
          value={fields.newPassword}
          onChange={(value) => setFields({ ...fields, newPassword: value })}
          error={errors.newPassword}
        />

        <Input
          label={"Confirm New Password"}
          name="confirmPassword"
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Re-enter new password"
          value={fields.confirmPassword}
          onChange={(e) =>
            setFields({ ...fields, confirmPassword: e.target.value })
          }
          startAdornment={<Lock size={16} />}
          endAdornment={
            <button
              type="button"
              onClick={() => setShowConfirmPassword((s) => !s)}
              className="flex items-center"
            >
              {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
        />

        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setFields({
                oldPassword: "",
                newPassword: "",
                confirmPassword: "",
              })
            }
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={changePassword?.loading}
          >
            Reset
          </Button>
        </div>
      </form>
    </>
  );
};

export default ResetPasswordCard;
